import log from 'loglevel';

export const LOG_LEVEL_OPTIONS = ['trace', 'debug', 'info', 'warn', 'error', 'silent'] as const;
export type LogLevelName = (typeof LOG_LEVEL_OPTIONS)[number];

const LOG_LEVEL_STORAGE_KEY = 'portal.logLevel';
const configuredLevel = process.env.REACT_APP_LOG_LEVEL;
const storedLevel = readStoredLogLevel();
const defaultLevel = storedLevel || readConfiguredLogLevel(configuredLevel) || 'warn';
const scopedLoggers = new Set<log.Logger>();

log.setDefaultLevel(defaultLevel);
log.setLevel(defaultLevel, false);

export function createScopedLogger(name: string): log.Logger {
	const scopedLogger = log.getLogger(name);
	scopedLogger.setLevel(getApplicationLogLevel(), false);
	scopedLoggers.add(scopedLogger);
	return scopedLogger;
}

export function getApplicationLogLevel(): LogLevelName {
	return levelNumberToName(log.getLevel());
}

export function setApplicationLogLevel(level: LogLevelName): void {
	log.setLevel(level, false);
	scopedLoggers.forEach((scopedLogger) => scopedLogger.setLevel(level, false));
	if (typeof window !== 'undefined') {
		window.localStorage.setItem(LOG_LEVEL_STORAGE_KEY, level);
	}
}

function readConfiguredLogLevel(level: string | undefined): LogLevelName | null {
	return level && LOG_LEVEL_OPTIONS.includes(level as LogLevelName) ? (level as LogLevelName) : null;
}

function readStoredLogLevel(): LogLevelName | null {
	if (typeof window === 'undefined') {
		return null;
	}

	const stored = window.localStorage.getItem(LOG_LEVEL_STORAGE_KEY);
	if (!stored) {
		return null;
	}

	const normalized = stored.replace(/^"|"$/g, '') as LogLevelName;
	return LOG_LEVEL_OPTIONS.includes(normalized) ? normalized : null;
}

function levelNumberToName(level: number): LogLevelName {
	switch (level) {
		case log.levels.TRACE:
			return 'trace';
		case log.levels.DEBUG:
			return 'debug';
		case log.levels.INFO:
			return 'info';
		case log.levels.WARN:
			return 'warn';
		case log.levels.ERROR:
			return 'error';
		case log.levels.SILENT:
			return 'silent';
		default:
			return 'warn';
	}
}

export default log;
